import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";
import { COMPANY } from "@/lib/company";

const LAST_UPDATED = "2026-06-09";

export const Route = createFileRoute("/adatkezelesi-tajekoztato")({
  head: () => ({
    meta: [
      { title: "Adatkezelési tájékoztató | LAKROSZ Autószerviz" },
      {
        name: "description",
        content:
          "A LAKROSZ Autószerviz adatkezelési tájékoztatója a GDPR (EU 2016/679) és az Infotv. szerint — autószervizre szabva.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Adatkezelési tájékoztató | LAKROSZ Autószerviz" },
      {
        property: "og:description",
        content: "GDPR és Infotv. szerinti adatkezelési tájékoztató autószervizre szabva.",
      },
      { property: "og:url", content: "https://lakroszautoszerviz.com/adatkezelesi-tajekoztato" },
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: "https://lakroszautoszerviz.com/adatkezelesi-tajekoztato" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout
      title="Adatkezelési tájékoztató"
      subtitle={`Utolsó frissítés: ${LAST_UPDATED}`}
    >
      <section>
        <h2 className="font-display text-2xl mt-4 mb-3">A) Adatkezelő adatai</h2>
        <div className="rounded-2xl border border-border bg-surface p-6 text-sm space-y-1">
          <div>
            <strong>Név:</strong>{" "}
            <span>{COMPANY.legalName}</span>
          </div>
          <div>
            <strong>Székhely:</strong>{" "}
            <span>{COMPANY.address.full}</span>
          </div>
          <div>
            <strong>Telephely:</strong> 3300 Eger, Vécseyvölgy utca 99.
          </div>
          <div>
            <strong>E-mail:</strong>{" "}
            <a className="text-primary" href={`mailto:${COMPANY.contact.email}`}>
              {COMPANY.contact.email}
            </a>
          </div>
          <div>
            <strong>Telefonszám:</strong>{" "}
            <a className="text-primary" href={COMPANY.contact.phoneHref}>
              {COMPANY.contact.phone}
            </a>
          </div>
          <div>
            <strong>Weboldal:</strong> lakroszautoszerviz.com
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">B) Kezelt adatok</h2>
        <p>
          A weboldal kapcsolatfelvételi űrlapján keresztül az alábbi személyes adatok
          kerülhetnek kezelésre:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>név</li>
          <li>e-mail cím</li>
          <li>telefonszám</li>
          <li>üzenet tartalma</li>
          <li>az érdeklődés tárgya</li>
          <li>időpont-egyeztetéssel kapcsolatos információk</li>
          <li>opcionálisan járművel kapcsolatos adatok, ha az ügyfél megadja</li>
          <li>
            audit célú technikai adatok: IP-cím, böngésző azonosító (user agent), beküldés
            időpontja
          </li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">C) Adatkezelés célja</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>kapcsolatfelvétel</li>
          <li>ajánlatadás</li>
          <li>időpont-egyeztetés</li>
          <li>ügyfélkommunikáció</li>
          <li>szolgáltatással kapcsolatos egyeztetés</li>
          <li>reklamáció vagy panasz kezelése</li>
          <li>jogi igények kezelése, ha szükséges</li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">D) Adatkezelés jogalapja</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>GDPR 6. cikk (1) bekezdés b) pont</strong> — szerződés előkészítése vagy
            teljesítése (időpontfoglalás, szolgáltatás teljesítése).
          </li>
          <li>
            <strong>GDPR 6. cikk (1) bekezdés f) pont</strong> — jogos érdek (pl.
            ügyfélkommunikáció, jogi igények kezelése, audit naplózás visszaélések
            megelőzéséhez).
          </li>
          <li>
            <strong>GDPR 6. cikk (1) bekezdés c) pont</strong> — jogi kötelezettség
            teljesítése (pl. számviteli, számlázási adatok megőrzése).
          </li>
          <li>
            <strong>GDPR 6. cikk (1) bekezdés a) pont</strong> — az érintett hozzájárulása a
            kapcsolatfelvételi űrlap elküldésekor.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">E) Adattárolás ideje</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>Kapcsolatfelvételi üzenetek: legfeljebb 12 hónap.</li>
          <li>Ajánlatkérések: legfeljebb 12 hónap.</li>
          <li>
            Panaszkezelési adatok: a jogvita lezárásáig, illetve jogszabályban
            meghatározott ideig.
          </li>
          <li>Számlázási adatok: a számviteli jogszabályok szerinti ideig.</li>
          <li>
            Szerződéses / javítással kapcsolatos dokumentumok: jogszabályban meghatározott
            ideig vagy jogos érdek fennállásáig.
          </li>
          <li>
            Audit célú technikai adatok (IP, user agent): a beküldéstől számított 6 hónap.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">F) Adatfeldolgozók</h2>
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-sm border border-border rounded-xl overflow-hidden min-w-[640px]">
            <thead className="bg-surface">
              <tr className="text-left">
                <th className="p-4 border-b border-border whitespace-nowrap">Adatfeldolgozó típusa</th>
                <th className="p-4 border-b border-border">Megnevezés</th>
              </tr>
            </thead>
            <tbody className="[&_td]:p-4 [&_td]:border-b [&_td]:border-border/60 [&_tr:last-child_td]:border-b-0">
              <tr>
                <td>Tárhelyszolgáltató</td>
                <td>Lovable Labs Incorporated</td>
              </tr>
              <tr>
                <td>E-mail szolgáltató</td>
                <td>Resend, Inc. – https://resend.com</td>
              </tr>
              <tr>
                <td>Weboldal üzemeltetője / fejlesztője</td>
                <td>Szacsva Balázs egyéni vállalkozó – FXS Marketing</td>
              </tr>
              <tr>
                <td>Űrlapkezelő rendszer</td>
                <td>Resend, Inc. – https://resend.com</td>
              </tr>
              <tr>
                <td>Analitikai / marketing szolgáltató</td>
                <td>A weboldal jelenleg nem használ külön analitikai vagy marketing célú mérőkódot.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">G) Érintetti jogok</h2>
        <p>A GDPR alapján az érintett az alábbi jogokkal élhet:</p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>hozzáférés joga</li>
          <li>helyesbítés joga</li>
          <li>törlés joga („elfeledtetéshez való jog”)</li>
          <li>adatkezelés korlátozásának joga</li>
          <li>tiltakozás joga</li>
          <li>adathordozhatóság joga</li>
          <li>panasztétel joga a felügyeleti hatóságnál</li>
          <li>hozzájárulás bármikori visszavonása (a visszavonás előtti kezelést nem érinti)</li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">H) Panasztételi lehetőség</h2>
        <p>
          Amennyiben úgy érzed, hogy az adatkezelés sérti a jogaidat, panaszt nyújthatsz be a
          Nemzeti Adatvédelmi és Információszabadság Hatóságnál (NAIH):
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          Weboldal:{" "}
          <a
            className="text-primary"
            href="https://www.naih.hu"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.naih.hu
          </a>
          <br />
          Ezen felül jogod van bírósághoz fordulni.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">I) Hírlevél</h2>
        <p>
          A weboldal jelenleg nem végez hírlevél-feliratkoztatást, és a kapcsolatfelvételi
          űrlapon megadott adatokat nem használja automatikus marketing célú e-mail küldésre.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">J) Adatfelhasználás célja</h2>
        <p>
          A weboldalon keresztül megadott adatok kizárólag az érdeklődés, ajánlatkérés,
          időpont-egyeztetés, ügyfélkommunikáció vagy panaszkezelés céljából kerülnek
          felhasználásra.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">K) Adatbiztonsági intézkedések</h2>
        <p>
          Az Adatkezelő az adatok biztonsága érdekében megfelelő technikai és szervezési
          intézkedéseket tesz: titkosított (HTTPS) adatátvitel, hozzáférés-korlátozás,
          szerver oldali validáció, audit naplózás.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl mt-8 mb-3">L) Módosítások</h2>
        <p>
          Az Adatkezelő fenntartja a jogot a tájékoztató módosítására. A mindenkor hatályos
          szöveg a weboldalon érhető el. Utolsó frissítés:{" "}
          <strong>{LAST_UPDATED}</strong>.
        </p>
      </section>
    </LegalLayout>
  );
}
